import { Auth } from "aws-amplify";
import { AlertColor } from "customEnums/AlertColor";
import { QueryRequestBody } from "interfaces/common";
import { PageNames } from "interfaces/config";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToastAlert } from "../common/alerts/useToastAlert";
import { handleUnauthorized } from "utils/handleUnauthorized";
import { RbSubAction } from "../customEnums/RbSubAction";
import { useRapbackMaintenanceStore } from '../pages/RapbackSubscriptions/RapbackMaintenance/state';
import { useNavStore, useTableStore } from "../state";
import { getCurrentEnv, LogConfig, logger } from "../utils";
import { generateRBSubscriptionQueryParams } from "../utils/queryParams/rapback";
import fetchConfirmPrenotificationData from './RapbackSubscriptionUtils/fetchConfirmPrenotificationData';
import fetchDenyPrenotificationData from './RapbackSubscriptionUtils/fetchDenyPrenotificationData';
import fetchMaintenanceData from './RapbackSubscriptionUtils/fetchMaintenanceData';
import fetchRenewdata from './RapbackSubscriptionUtils/fetchRenewdata';
import fetchUnsubscribeData from './RapbackSubscriptionUtils/fetchUnsubscribeData';
import fetchUpdateSubscriptionData from './RapbackSubscriptionUtils/fetchUpdateSubscriptionData';
import getRunQuery from './RapbackSubscriptionUtils/getRunQuery';
import { useAuth } from "./useAuth";

export const getCount = (obj: object) => {
    return Object.values(obj).filter(value => value === true).length
}

export const useRapbackSubscription = (
    pageName: string,
    pageSize: number,
    currentPage: number,
    userRole: any,
    searchParams: any,
    auditSearch: boolean,
    tableName: string = "",
    calledFromAuthHook: boolean = false
) => {

    const STATE = getCurrentEnv
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState()
    const [mappedData, setMappedData] = useState<any>()
    const [totalRecords, setTotalRecords] = useState(0);
    const { notifySuccess, notifyError, notifyInfo, notifyWarning } = useToastAlert();
    const fullName = useRapbackMaintenanceStore(state => state.fullName)
    const columnState = useTableStore(state => state.columnState)
    const tableConfig = useTableStore(state => state.tableConfig)
    const runFilters = useTableStore(state => state.runFilters)
    const setRunFilters = useTableStore(state => state.setRunFilters)
    const runCount = useRef(0);
    const pageRef = useRef(currentPage)
    const pageSizeRef = useRef(pageSize)
    const [refetchParams, setRefetchParams] = useState<any>({})
    const { logout } = useAuth();
    const pagination = useTableStore(state => state.pagination)
    const { order, orderBy } = pagination[tableName]
    const orderByRef = useRef(orderBy)
    const orderRef = useRef(order)
    const currentPageName = useTableStore(state => state.pageName);

    const initialInvocation = useNavStore(state => state.initialInvocation)
    const updatePrenotifications = useNavStore(state => state.updatePrenotifications)
    const updateValidations = useNavStore(state => state.updateValidations)
    const setInitialInvocationFalse = useNavStore(state => state.setInitialInvocationFalse)

    console.log('run useRBSub orderBy in query ', { orderBy, pageName })
    const statusFilters = columnState.page["rapback-maintenance"]!.filters?.find(filter => filter.filterLabel === "Filter Status")
    const timeFilters = columnState.page["rapback-validations"]!.filters?.find(filter => filter.filterLabel === "Time Remaining")

    const setUINotification = (title: string, alertMessage: string, severity: AlertColor, shouldOpenNotification: boolean, shouldReceiveResponse: boolean) => {
        if (shouldOpenNotification) {
            switch (severity) {
                case AlertColor.SUCCESS:
                    notifySuccess(title, alertMessage);
                    break;
                case AlertColor.ERROR:
                    notifyError(title, alertMessage);
                    break;
                case AlertColor.WARNING:
                    notifyWarning(title, alertMessage);
                    break;
                case AlertColor.INFO:
                default:
                    notifyInfo(title, alertMessage);
                    break;
            }
        }
    }

    const fetchMaintenance = useCallback(async (queryDataRequest: any, action?: RbSubAction | undefined, currentSearchParams: any[] = []) => {
        console.log('debug rb sub useEffect dependencies: useRapbacksubscription fetchMaintenance: ', initialInvocation)
        try {
            setLoading(true)
            let outstandingPrenotificationsNum;
            let outstandingValidationsNum;

            const data = await fetchMaintenanceData(queryDataRequest, action);

            console.log('useRapbacksubscription data: ', { rqueriesReultsLength: data['queriesResults'].length > 1, initialInvocation, data })

            handleUnauthorized(data.message, logout);
            setTotalRecords(data['queriesResults'][0].fullCount)

            if (initialInvocation) {
                outstandingPrenotificationsNum = data['queriesResults'][0].fullCount
                updatePrenotifications(outstandingPrenotificationsNum)
                outstandingValidationsNum = data['queriesResults'][1].fullCount
                updateValidations(outstandingValidationsNum)
                setInitialInvocationFalse()
            } else {
                const { results } = data['queriesResults'][0];

                console.log('useRapbacksubscription NOT initial request: ', { results, action, pageName })

                if (pageName === PageNames.RAPBACK_PRENOTIFICATIONS || action === RbSubAction.CONFRIM_PRENOTIFICATION || action === RbSubAction.DENY_PRENOTIFICATION) {
                    updatePrenotifications(data['queriesResults'][0].fullCount)
                } else if (pageName === PageNames.RAPBACK_VALIDATIONS || action === RbSubAction.RENEW || action === RbSubAction.UNSUBSCRIBE) {
                    updateValidations(data['queriesResults'][0].fullCount)
                }

                const fullNameParam = currentSearchParams.find((param: any) => param.column === 'fullName');
                const filteredResults = fullNameParam?.value
                    ? results.filter((record: any) =>
                        record.fullName?.toLowerCase().includes(fullNameParam.value.toLowerCase().trim())
                    )
                    : results;

                setMappedData(filteredResults);
                setLoading(false);
            }

        } catch (err: any) {
            console.error('error querying rapback subs: ', err)
            setError(err);
            setLoading(false);
        }
    }, [initialInvocation])

    const handleConfirmPrenotification = async (subscriptionId: string) => {
        console.log(`handleConfirmPrenotification ran`)
        const { username } = await Auth.currentAuthenticatedUser();
        try {
            const resp = await fetchConfirmPrenotificationData(subscriptionId, username)

            if (resp.ok) {
                fetchMaintenance(refetchParams.queryDataRequest, RbSubAction.CONFRIM_PRENOTIFICATION, searchParams)
                setUINotification("Request Submitted",
                    `A pre-notification acceptance request has been submitted for ${fullName}. View pending details on the Rapback Maintenance screen.`,
                    AlertColor.SUCCESS, true, true)
            } else {
                setUINotification("Request Failed",
                    `Please try again to accept ${fullName}'s subscription`,
                    AlertColor.ERROR, true, true)
            }
        } catch (err: any) {
            setError(err)
        }
    }

    const handleDenyPrenotification = async (subscriptionId: string) => {
        const { username } = await Auth.currentAuthenticatedUser();

        try {
            const resp = await fetchDenyPrenotificationData(subscriptionId, username)

            if (resp.ok) {
                fetchMaintenance(refetchParams.queryDataRequest, undefined, searchParams)
                setUINotification("Request Submitted",
                    `A pre-notification rejection request has been submitted for ${fullName}. View pending detials on the Rapback Maintenance Screen.`,
                    AlertColor.SUCCESS, true, true)

            } else {
                setUINotification("Request Failed",
                    `Please try again to decline ${fullName}'s subscription`,
                    AlertColor.ERROR, true, true)
            }
        } catch (err: any) {
            setError(err)
        }
    }

    const handleUpdateSubscription = async (subscriptionId: string, expirationDate?: string, dob?: string) => {
        const { username } = await Auth.currentAuthenticatedUser();

        try {
            const resp = await fetchUpdateSubscriptionData(subscriptionId, username, expirationDate, dob)
            if (resp.ok) {
                fetchMaintenance(refetchParams.queryDataRequest, undefined, searchParams)
                setUINotification("Request Successfully Submitted",
                    `Edit Requests will be reviewed`,
                    AlertColor.SUCCESS, true, true);

            } else {
                setUINotification("Request Error",
                    `Your attempt to edit has failed, please try again or contact support`,
                    AlertColor.ERROR, true, true);
            }
        } catch (err: any) {
            setError(err)
        }
    }

    const handleUnsubscribe = async (subscriptionId: string) => {
        const { username } = await Auth.currentAuthenticatedUser();

        try {
            const resp = await fetchUnsubscribeData(subscriptionId, username, pageName)
            console.log('unsubscribe response: ', resp)
            if (resp.ok) {
                if (pageName === "rapback-maintenance") {
                    fetchMaintenance(refetchParams.queryDataRequest, undefined, searchParams)
                    setUINotification("Request Successfully Submitted",
                        `Unsubscribe request will be reviewed. If approved, ${fullName} will be unsubscribed from receiving Rapbacks.`,
                        AlertColor.SUCCESS, true, true);
                }
                if (pageName === "rapback-validations") {
                    setUINotification("Request Submitted",
                        `A rapback subscription cancellation request has been submitted for ${fullName}. View pending detials on the Rapback Maintenance Screen.`,
                        AlertColor.SUCCESS, true, true);
                }
                console.log('debug refetch unsubscribe: ', refetchParams)
                return resp.ok
            } else {
                if (pageName === "rapback-maintenance") {
                    setUINotification("Request Error",
                        `Your attempt to unsubscribe ${fullName} has failed, please try again or contact support.`,
                        AlertColor.ERROR, true, true);
                }
                if (pageName === "rapback-validations") {
                    setUINotification(`Request Failed`,
                        `Please try again to cancel ${fullName}'s subscription`,
                        AlertColor.ERROR, true, true);
                }
                return false
            }
        } catch (err: any) {
            setError(err)
        }
    }

    const handleRenew = async (subscriptionId: string) => {
        console.log(`Validations handleRenew hook subscriptionId: ${subscriptionId}`)
        const { username } = await Auth.currentAuthenticatedUser();

        try {
            console.log(`Validations handleRenew hook fetching....`)
            const resp = await fetchRenewdata(subscriptionId, username)

            if (resp.ok) {
                fetchMaintenance(refetchParams.queryDataRequest, RbSubAction.RENEW, searchParams)
                setUINotification(`Request Submitted`,
                    `A rapback subscription renewal request has been submitted for ${fullName}. View pending details on the Rapback Maintenance screen.`,
                    AlertColor.SUCCESS, true, true);
            } else {
                setUINotification(`Request Failed`,
                    `Please try again to renew ${fullName}'s subscription`,
                    AlertColor.ERROR, true, true);
            }
            return resp.ok
        } catch (err: any) {
            setError(err)
        }
    }

    useEffect(() => {
        console.log('1010101 debug rb sub useEffect dependencies: ', {
            STATE,
            userRole,
            pageSize,
            currentPage,
            order,
            orderBy,
            searchParams,
            runFilters,
            columnState: columnState.page[pageName][tableName]?.filters?.length,
            statusFilters,
            timeFilters,
            tableName,
            initialInvocation,
            pageName,
            calledFromAuthHook,
            tableConfig: tableConfig[tableName],
            runCountCurrent: runCount.current,
            pageChanged: (pageRef.current !== currentPage || (pageRef.current === 1 && currentPage === 1) || pageSizeRef.current !== pageSize) || (orderByRef.current !== orderBy || orderRef.current !== order)
        })
        runCount.current = runCount.current + 1;
        if (currentPageName !== pageName && !initialInvocation) {
            console.log(`useRapbackSubscription: Not on ${pageName} page (currently on ${currentPageName}), skipping query`);
            return
        }
        //update column names for dates
        const dateIndexs = [
            "expirationDate",
            "lowerDate",
            "upperDate",
            "dob",
            "subscriptionDate",
            "deletionDate",
            "receivedDate"
        ]
        const param = searchParams.find((param) => dateIndexs.includes(param.column))

        let filters = columnState.page[pageName][tableName]?.filters
        const filterLabels: any[] = []
        let filterNumbers: any[] = []
        if (filters) {
            filters.forEach(filter => {
                let filterValueKeys = Object.keys(filter.filterValues);
                filterValueKeys.forEach(key => {
                    const { boolNumber, selected, label } = filter.filterValues[key];
                    if (pageName === "rapback-validations") {
                        filterNumbers.push(boolNumber);
                    }
                    if (selected) {
                        if (pageName === "rapback-maintenance") {
                            filterLabels.push(label);
                        } else if (pageName === "rapback-validations") {
                            filterLabels.push(boolNumber);
                        }
                    }
                });
            });
        }

        const runQueryConfig: any = {
            refComps: {
                runCount: runCount.current,
                pageRef: pageRef.current,
                currentPage,
                pageSizeRef: pageSizeRef.current,
                pageSize,
                orderByRef: orderByRef.current,
                orderBy,
                orderRef: orderRef.current,
                order
            },
            dependencies: {
                STATE,
                initialInvocation,
                pageSize,
                currentPage,
                order,
                orderBy,
                tableName,
                userRole,
                tableConfig,
                columnStateLength: columnState.page[pageName][tableName]?.filters?.length,
                runFilters,
                searchParams,
                calledFromAuthHook
            },
            caughtOldSearch: searchParams?.length > 0 && runCount.current <= 2,
            pageChanged: (pageRef.current !== currentPage || (pageRef.current === 1 && currentPage === 1) || pageSizeRef.current !== pageSize) || (orderByRef.current !== orderBy || orderRef.current !== order),
            runCountOverTwo: runCount.current > 2 && runFilters,
            searchingNonRbPage: searchParams?.length > 0 && pageName !== PageNames.RAPBACK_MAINTENANCE && pageName !== PageNames.RAPBACK_VALIDATIONS && pageName !== PageNames.RAPBACK_PRENOTIFICATIONS,
            filterLabels,
            pageName,
        }

        let runQuery = getRunQuery(runQueryConfig)
        console.log('1010101 debug rb sub useEffect dependencies debug rb sub ', { runQuery })

        if (runQuery) {
            let queryDataRequest: QueryRequestBody = {
                "queries": initialInvocation ? [
                    {
                        "queryParams": [...generateRBSubscriptionQueryParams('rapback-prenotifications', searchParams, filterLabels, filterNumbers, 'subscriptionDate', order, param)],
                        "limit": pageSize,
                        "offset": (currentPage - 1) * pageSize,
                        "queryName": "queryRapbackSubscriptionTable",
                        "transformData": true

                    },
                    {
                        "queryParams": [...generateRBSubscriptionQueryParams('rapback-validations', searchParams, filterLabels, filterNumbers, 'daysToExpire', 'desc', param)],
                        "limit": pageSize,
                        "offset": (currentPage - 1) * pageSize,
                        "queryName": "queryRapbackSubscriptionTable",
                        "transformData": true
                    },
                ] : [
                    {
                        "queryParams": [...generateRBSubscriptionQueryParams(pageName, searchParams, filterLabels, filterNumbers, orderBy, order, param)],
                        "limit": pageSize,
                        "offset": (currentPage - 1) * pageSize,
                        "queryName": "queryRapbackSubscriptionTable",
                        "transformData": true
                    }
                ]
            };

            const debugGetRunQuery: LogConfig = {
                identifier: "debug rb sub",
                pageName,
                thingToDebug: "runQuery",
                paramsToLog: {
                    conditionA: tableConfig[tableName]?.filter && filterLabels.length < 1,
                    conditionB: initialInvocation && !runQueryConfig.searchingNonRbPage,
                    conditionC: runQueryConfig.searchingNonRbPage,
                    conditionD: runQueryConfig.caughtOldSearch,
                    conditionE: runQueryConfig.pageChanged,
                    conditionF: !initialInvocation,
                    isPrenotification: pageName === PageNames.RAPBACK_PRENOTIFICATIONS,
                    runPrenotificationQuery: userRole.length > 1,
                    runMaintenanceValidationsQuery: userRole && filterLabels.length > 0,
                    runQuery,
                    initialInvocation,
                    tableConfig
                }
            }
            // console.log utility
            logger(debugGetRunQuery)
            if (STATE === "ga" || STATE === "mc") {
                console.log('debug rb maintenance calling fetching maintence data ', STATE === 'ga')
                setRefetchParams({ queryDataRequest })
                if (!calledFromAuthHook) {
                    console.log('debug rb sub useEffect dependencies before fetchMaintenance call: ', { initialInvocation, calledFromAuthHook, runFetchMaintenance: !calledFromAuthHook })

                    fetchMaintenance(queryDataRequest, undefined, searchParams)
                }
            }
        }
        setRunFilters(false)
        console.log('debug searchParams: ', searchParams)
    }, [
        currentPageName,
        STATE,
        userRole,
        pageSize,
        currentPage,
        order,
        orderBy,
        searchParams,
        runFilters,
        columnState.page[pageName][tableName]?.filters?.length,
        tableName,
        initialInvocation,
        initialInvocation ? tableConfig["transaction-sumarry"] : tableConfig[tableName],
        calledFromAuthHook
    ]
    )
    return { data: mappedData, runCount: runCount.current, error, loading, totalRecords, fetchMaintenance, refetchParams, handleConfirmPrenotification, handleDenyPrenotification, handleUpdateSubscription, handleUnsubscribe, handleRenew };
}
